import React, { useState } from "react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, X, ArrowLeft, Check } from "lucide-react";
import { fmtPrice, createOrder } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { fbqTrack } from "@/lib/pixel";
import { toast } from "sonner";

const empty = { customer_name: "", email: "", phone: "", address: "", city: "", county: "", postal_code: "", notes: "" };

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal, shipping, total, clear, pendingCheckout, setPendingCheckout } = useCart();
  const [step, setStep] = useState("cart"); // cart | checkout | done
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  React.useEffect(() => {
    if (open && pendingCheckout && items.length > 0) {
      setStep("checkout");
      setPendingCheckout(false);
    }
  }, [open, pendingCheckout, items.length, setPendingCheckout]);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const phoneDigits = (form.phone || "").replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      toast.error("Număr de telefon invalid", { description: "Introdu un număr de telefon valid (minim 10 cifre)." });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity })),
      };
      if (!payload.email || !payload.email.trim()) delete payload.email;
      const order = await createOrder(payload);
      fbqTrack("Purchase", {
        value: Number((order.total ?? total).toFixed(2)),
        currency: "RON",
        content_type: "product",
        content_ids: items.map((i) => i.product_id),
        contents: items.map((i) => ({ id: i.product_id, quantity: i.quantity, item_price: i.price })),
        num_items: items.reduce((s, i) => s + i.quantity, 0),
      });
      setConfirmed(order);
      setStep("done");
      clear();
    } catch (err) {
      toast.error("Comanda nu a putut fi plasată", { description: "Verifică datele și încearcă din nou." });
    } finally {
      setLoading(false);
    }
  };

  const goToCheckout = () => {
    fbqTrack("InitiateCheckout", {
      value: Number(total.toFixed(2)),
      currency: "RON",
      content_ids: items.map((i) => i.product_id),
      num_items: items.reduce((s, i) => s + i.quantity, 0),
    });
    setStep("checkout");
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => { setStep("cart"); setForm(empty); setConfirmed(null); }, 300);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <SheetContent
        side="right"
        data-lenis-prevent
        className="w-full sm:max-w-md p-0 bg-[#EDEAE3] border-l border-black/10 text-[#0E0F12] [&>button]:hidden flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetTitle className="sr-only">Coșul de cumpărături</SheetTitle>
        <SheetDescription className="sr-only">Produsele selectate și finalizarea comenzii</SheetDescription>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-black/10">
          {step === "checkout" ? (
            <button data-testid="checkout-back" onClick={() => setStep("cart")} className="flex items-center gap-2 text-sm text-[#4A4844] hover:text-[#0E0F12] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Înapoi
            </button>
          ) : (
            <h3 className="font-serif text-2xl">{step === "done" ? "Mulțumim" : "Coșul tău"}</h3>
          )}
          <button data-testid="cart-close" onClick={close} className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center text-[#4A4844] hover:text-[#0E0F12] transition-colors">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* CART */}
        {step === "cart" && (
          <>
            <div data-lenis-prevent className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-6 py-5">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20">
                  <p className="font-serif text-3xl mb-3">Coș gol</p>
                  <p className="text-[#4A4844] text-sm">Adaugă parfumul preferat pentru rufe.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {items.map((i) => (
                    <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-4">
                      <img src={i.image} alt={i.name} className="w-20 h-24 object-cover rounded-sm border border-black/10" />
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <h4 className="font-serif text-lg leading-tight">{i.name}</h4>
                          <button data-testid={`cart-remove-${i.product_id}`} onClick={() => remove(i.product_id)} className="text-[#4A4844] hover:text-[#0E0F12] transition-colors">
                            <Trash2 className="w-4 h-4" strokeWidth={1.4} />
                          </button>
                        </div>
                        <p className="text-xs text-[#4A4844] mt-0.5">{i.scent_label}</p>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-black/12 rounded-sm">
                            <button data-testid={`cart-dec-${i.product_id}`} onClick={() => setQty(i.product_id, i.quantity - 1)} className="w-8 h-8 flex items-center justify-center text-[#4A4844] hover:text-[#0E0F12]">
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="w-8 text-center text-sm">{i.quantity}</span>
                            <button data-testid={`cart-inc-${i.product_id}`} onClick={() => setQty(i.product_id, i.quantity + 1)} className="w-8 h-8 flex items-center justify-center text-[#4A4844] hover:text-[#0E0F12]">
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <span className="text-sm">{fmtPrice(i.price * i.quantity)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="shrink-0 border-t border-black/10 px-6 py-6 space-y-4">
                <Row label="Subtotal" value={fmtPrice(subtotal)} />
                <Row label="Livrare" value="Gratuită" muted />
                <p className="text-[11px] text-[#4A4844]">Transport gratuit la toate comenzile.</p>
                <div className="flex justify-between items-baseline pt-2 border-t border-black/10">
                  <span className="text-sm tracking-widest uppercase text-[#4A4844]">Total</span>
                  <span data-testid="cart-total" className="font-serif text-2xl">{fmtPrice(total)}</span>
                </div>
                <button
                  data-testid="go-to-checkout"
                  onClick={goToCheckout}
                  className="w-full py-4 bg-[#0E0F12] text-[#EDEAE3] text-sm tracking-widest uppercase rounded-sm hover:-translate-y-[2px] transition-transform duration-300"
                >
                  Finalizează comanda
                </button>
              </div>
            )}
          </>
        )}

        {/* CHECKOUT */}
        {step === "checkout" && (
          <form onSubmit={submit} className="flex-1 min-h-0 flex flex-col" data-testid="checkout-form">
            <div data-lenis-prevent className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-6 py-5 space-y-4">
              <p className="text-xs tracking-[0.3em] uppercase text-gramur">Date de livrare</p>
              <Field label="Nume complet" value={form.customer_name} onChange={change("customer_name")} testid="f-name" required />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Email (opțional)" type="email" value={form.email} onChange={change("email")} testid="f-email" />
                <Field label="Telefon" type="tel" inputMode="numeric" maxLength={10} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))} testid="f-phone" required />
              </div>
              <Field label="Adresă" value={form.address} onChange={change("address")} testid="f-address" required />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Oraș" value={form.city} onChange={change("city")} testid="f-city" required />
                <Field label="Județ" value={form.county} onChange={change("county")} testid="f-county" required />
              </div>
              <Field label="Cod poștal (opțional)" value={form.postal_code} onChange={change("postal_code")} testid="f-postal" />
              <Field label="Observații (opțional)" value={form.notes} onChange={change("notes")} testid="f-notes" />
            </div>
            <div className="shrink-0 border-t border-black/10 px-6 py-6 space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-sm tracking-widest uppercase text-[#4A4844]">Total</span>
                <span className="font-serif text-2xl">{fmtPrice(total)}</span>
              </div>
              <p className="text-[11px] text-[#4A4844]">Plata la livrare (ramburs). Fără plată online în acest moment.</p>
              <button
                type="submit"
                disabled={loading}
                data-testid="place-order-button"
                className="w-full py-4 bg-gramur text-[#0E0F12] text-sm tracking-widest uppercase rounded-sm hover:-translate-y-[2px] transition-transform duration-300 disabled:opacity-60"
              >
                {loading ? "Se plasează..." : "Plasează comanda"}
              </button>
            </div>
          </form>
        )}

        {/* DONE */}
        {step === "done" && confirmed && (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8" data-testid="order-confirmation">
            <div className="w-16 h-16 rounded-full border border-gramur flex items-center justify-center mb-8">
              <Check className="w-7 h-7 text-gramur" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif text-4xl mb-3">Comandă plasată</h3>
            <p className="text-[#4A4844] text-sm max-w-xs">
              Îți mulțumim, {confirmed.customer_name.split(" ")[0]}. Comanda ta a fost înregistrată.
            </p>
            <p className="mt-6 text-xs tracking-[0.3em] uppercase text-[#4A4844]">Număr comandă</p>
            <p data-testid="order-number" className="font-serif text-2xl text-gramur mt-1">{confirmed.order_number}</p>
            <p className="mt-6 text-sm">Total: {fmtPrice(confirmed.total)}</p>
            <button onClick={close} className="mt-12 px-9 py-4 bg-[#0E0F12] text-[#EDEAE3] text-sm tracking-widest uppercase rounded-sm hover:-translate-y-[2px] transition-transform duration-300">
              Continuă
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-[#4A4844]">{label}</span>
      <span className={muted ? "text-[#4A4844]" : ""}>{value}</span>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, inputMode, maxLength }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.2em] uppercase text-[#4A4844] mb-2">{label}</span>
      <input
        data-testid={testid}
        type={type}
        inputMode={inputMode}
        maxLength={maxLength}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-b border-black/12 focus:border-gramur outline-none py-2.5 text-[#0E0F12] text-sm transition-colors"
      />
    </label>
  );
}
